import { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import {
  Colors,
  ColorValues,
  Flex,
  IconShare,
  Typography,
} from '@banhala/ui-kit';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useGoodsDetail } from '../../../../queries/useGoodsDetail';
import { useAffiliateGoods } from '../../../../queries/useAffiliateGoods';
import {
  useGrowthBookFeatureValue,
  useInstanceId,
  useScreenshot,
} from 'shared/src/hooks';
import {
  bridge,
  isAndroid,
  isNil,
  isNotNil,
  isWeb,
  localizeNumber,
} from 'shared/src/utils';
import { scheduleOnRN } from 'react-native-worklets';
import { useLogClickShareGoods } from './hooks/useLogClickShareGoods';
import { useLogGoodsDefaultAnalyticsParams } from '../../../../hooks/useLogGoodsAnalyticsEvent';

const CLOSED_BUTTON_SIZE = isAndroid ? 46 : 41;
// 측정 전 초기 fallback 값
const OPENED_BUTTON_SIZE_FULL = isAndroid || isWeb ? 92 : 80;
// 초기 노출 애니메이션 총 소요시간 (ms): 200 + 200 + (1200+200) + 200
const INITIAL_ANIMATION_DURATION = 2000;
// 반복 사이클 노출 시간 (ms): 200(expand) + 1500(display) + 200+200(collapse)
const RECURRING_CYCLE_DURATION = 2100;
// 반복 사이클 대기 시간 (ms)
const RECURRING_GAP = 2500;

export const ShareRewardButton: React.FC<{ showNudging: boolean }> = props => {
  const animValue = useSharedValue<number>(CLOSED_BUTTON_SIZE);
  const getDefaultAnalyticsParams = useLogGoodsDefaultAnalyticsParams();
  const [reward, setReward] = useState(0);
  const [isFullReward, setIsFullReward] = useState(true);
  const [showBlueBg, setShowBlueBg] = useState(false);
  const [rewardForMeasure, setRewardForMeasure] = useState<number>(0);

  const recurringTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // 측정된 실제 텍스트 너비 (fallback: 상수 값)
  const fullWidthRef = useRef<number>(OPENED_BUTTON_SIZE_FULL);
  const rewardForMeasureRef = useRef<number>(0);
  // 실험 값 — 애니메이션 콜백 내 stale closure 방지용 ref
  const isExp10010OnRef = useRef<boolean>(false);
  const isExp10011OnRef = useRef<boolean>(false);

  const logClickShareGoods = useLogClickShareGoods();
  const instanceId = useInstanceId();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: affiliateGoods } = useAffiliateGoods();

  // exp-10011: 파란 배경 노출 (B/C 그룹)
  const isExp10010Value = useGrowthBookFeatureValue<boolean>('exp-10010');
  const isExp10011Value = useGrowthBookFeatureValue<{ exp_group: string }>(
    'exp-10011',
  );
  const isExp10011On = useMemo(
    () =>
      isExp10011Value?.exp_group != null &&
      ['B', 'C'].includes(isExp10011Value.exp_group),
    [isExp10011Value],
  );

  // 매 렌더마다 최신 실험 값을 ref에 동기화 (콜백에서 stale 방지)
  isExp10010OnRef.current = isExp10010Value === true;
  isExp10011OnRef.current = isExp10011On;

  useEffect(() => {
    return () => {
      if (recurringTimerRef.current) clearTimeout(recurringTimerRef.current);
      if (animationStartTimerRef.current)
        clearTimeout(animationStartTimerRef.current);
    };
  }, []);

  useScreenshot({
    instanceId,
    onCapture: () => {
      if (isNotNil(goodsDetail)) {
        bridge.openGoodsShare({
          is_by_screenshot: true,
          goods_sno: goodsDetail.goods.sno,
          affiliate_reward: {
            expected_reward: affiliateGoods?.expected_reward ?? 0,
            share_nudging: affiliateGoods?.share_nudging ?? null,
            logging: affiliateGoods?.logging ?? null,
          },

          logging_analytics: {
            ...getDefaultAnalyticsParams(),
          },
        });
      }
    },
  });

  // 숨겨진 측정 뷰의 onLayout 콜백에서 호출 — 두 뷰가 모두 측정되면 애니메이션 시작
  const tryStartAnimation = () => {
    if (animationStartTimerRef.current != null) return;

    if (isWeb) return;

    animationStartTimerRef.current = setTimeout(() => {
      setIsFullReward(true);

      const r = rewardForMeasureRef.current;
      const fullW = fullWidthRef.current;
      const expOn = isExp10010OnRef.current;
      const blueOn = isExp10011OnRef.current;

      // 초기 애니메이션: 닫힌 상태 유지(200ms) → 리워드 세팅 → 열기(200ms) → 유지(1200ms) → 닫기(200ms+200ms)
      animValue.value = withSequence(
        withTiming(
          CLOSED_BUTTON_SIZE,
          { duration: 200, easing: Easing.inOut(Easing.quad) },
          () => {
            scheduleOnRN(setReward, r);
            scheduleOnRN(setShowBlueBg, blueOn);
          },
        ),
        withTiming(fullW, {
          duration: 200,
          easing: Easing.inOut(Easing.circle),
        }),
        withDelay(
          1200,
          withTiming(fullW, { duration: 200 }, () => {
            scheduleOnRN(setReward, 0);
            scheduleOnRN(setShowBlueBg, false);
          }),
        ),
        withTiming(CLOSED_BUTTON_SIZE, {
          duration: 200,
          easing: Easing.inOut(Easing.quad),
        }),
      );

      // exp-10010: 초기 애니메이션 종료 후 반복 사이클 시작
      if (expOn && r > 0) {
        const scheduleRecurring = (delay: number) => {
          recurringTimerRef.current = setTimeout(() => {
            const shortW = fullWidthRef.current - 20;

            setIsFullReward(false);
            setShowBlueBg(blueOn);
            animValue.value = withSequence(
              withTiming(
                shortW,
                { duration: 200, easing: Easing.inOut(Easing.circle) },
                () => {
                  scheduleOnRN(setReward, r);
                },
              ),
              withDelay(
                1500,
                withTiming(shortW, { duration: 200 }, () => {
                  scheduleOnRN(setReward, 0);
                  scheduleOnRN(setShowBlueBg, false);
                }),
              ),
              withTiming(CLOSED_BUTTON_SIZE, {
                duration: 200,
                easing: Easing.inOut(Easing.quad),
              }),
            );
            scheduleRecurring(RECURRING_CYCLE_DURATION + RECURRING_GAP);
          }, delay);
        };
        scheduleRecurring(INITIAL_ANIMATION_DURATION + RECURRING_GAP);
      }
    }, 500);
  };

  useEffect(() => {
    if (isNil(goodsDetail)) return;
    const expectedReward = affiliateGoods?.expected_reward ?? 0;

    if (expectedReward === 0) return;
    // 측정 뷰를 렌더링하여 onLayout으로 실제 텍스트 너비를 측정한 뒤 애니메이션 시작
    rewardForMeasureRef.current = expectedReward;
    setRewardForMeasure(expectedReward);
  }, [goodsDetail, affiliateGoods]);

  const styles = useAnimatedStyle(() => {
    return {
      width: animValue.value,
      height: 16,
      overflow: 'hidden',
    };
  });

  return (
    <>
      {/* "최대 X원 받기" 너비 측정용 숨겨진 뷰 */}
      {rewardForMeasure > 0 && (
        <View
          style={{ position: 'absolute', opacity: 0 }}
          pointerEvents="none"
          onLayout={e => {
            fullWidthRef.current = Math.ceil(e.nativeEvent.layout.width);
            tryStartAnimation();
          }}
        >
          <Typography type={'subtitle4'}>
            최대{' '}
            <Typography type={'subtitle4'} color={'content_blue_primary'}>
              {localizeNumber(rewardForMeasure)}원
            </Typography>{' '}
            받기
          </Typography>
        </View>
      )}
      <Flex.Row
        padding={{ horizontal: 10, vertical: 6 }}
        gap={4}
        backgroundColor={
          props.showNudging ? undefined : showBlueBg ? 'blue5' : 'bg_secondary'
        }
        backgroundColorValue={props.showNudging ? ColorValues.pink3 : undefined}
        borderWidth={props.showNudging ? 1 : undefined}
        borderColor={props.showNudging ? 'border_secondary' : undefined}
        borderRadius={'medium'}
        overflow={'hidden'}
        onPress={() => {
          logClickShareGoods();
          if (isNotNil(goodsDetail)) {
            bridge.openGoodsShare({
              is_by_screenshot: false,
              goods_sno: goodsDetail.goods.sno,
              affiliate_reward: {
                expected_reward: affiliateGoods?.expected_reward ?? 0,
                share_nudging: affiliateGoods?.share_nudging ?? null,
                logging:
                  affiliateGoods?.logging != null
                    ? affiliateGoods.logging
                    : undefined,
              },
              logging_analytics: {
                ...getDefaultAnalyticsParams(),
              },
            });
          }
        }}
        testID={'공유버튼'}
      >
        <IconShare size={16} fill={Colors.content_secondary} />
        <Animated.View style={styles}>
          {reward > 0 ? (
            <Typography type={'subtitle4'}>
              최대{' '}
              <Typography
                type={'subtitle4'}
                color={
                  props.showNudging
                    ? 'content_pink_primary'
                    : 'content_blue_primary'
                }
              >
                {localizeNumber(reward)}원
              </Typography>
              {isFullReward ? ' 받기' : ''}
            </Typography>
          ) : (
            <Typography type={'subtitle4'}>공유하기</Typography>
          )}
        </Animated.View>
      </Flex.Row>
    </>
  );
};
